// ============================================
// components/auth/Signup.js
// ============================================
import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Container, TextField, 
  Button, Grid, Paper, Box, CssBaseline, Alert, InputAdornment, IconButton, Link as MuiLink, Stack 
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import theme from '../../styles/theme';
import { authService } from '../../services/authService';
import { ROUTES, ERRORS } from '../../utils/constants';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();

    // Validation
    if (
      firstName.trim() === '' ||
      lastName.trim() === '' ||
      email.trim() === '' ||
      password === '' ||
      confirmPassword === ''
    ) {
      setError(ERRORS.REQUIRED_FIELDS);
      return;
    }

    if (password !== confirmPassword) {
      setError(ERRORS.PASSWORDS_DONT_MATCH);
      return;
    }

    if (password.length < 6) {
      setError(ERRORS.PASSWORD_TOO_SHORT);
      return;
    }

    // Set a short-lived grace flag so PublicRoute doesn't immediately
    // redirect away from auth pages while we show success messaging.
    const isBrowser = typeof window !== 'undefined';
    if (isBrowser) {
      try { window.sessionStorage.setItem('authPublicRouteGrace', '1'); } catch (_) {}
    }

    setLoading(true);
    const result = await authService.signup(email, password, firstName, lastName);
    setLoading(false);

    if (result.success) {
      setSuccess(true);
      setError(null);
      
      // Navigate to homepage after short delay
      setTimeout(() => {
        if (isBrowser) {
          try { window.sessionStorage.removeItem('authPublicRouteGrace'); } catch (_) {}
        }
        navigate(ROUTES.HOME);
      }, 2000);
    } else {
      setError(result.error);
      // Clear grace flag on failure
      if (isBrowser) {
        try { window.sessionStorage.removeItem('authPublicRouteGrace'); } catch (_) {}
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar component="header" position="sticky" color="transparent" elevation={0} sx={{
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(17, 24, 39, 0.4)',
        borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}>
        <Toolbar variant="dense" sx={{ minHeight: 48, px: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Porchlight
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" component={RouterLink} to={ROUTES.LOGIN}>
            Login
          </Button>
        </Toolbar>
      </AppBar>
      <Container component="main" aria-labelledby="signup-page-title" maxWidth="xs" sx={{ px: 2 }}>
        {/* Visually hidden page heading for accessibility (inside main landmark) */}
        <Box component="h1" id="signup-page-title" sx={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: 1
        }}>
          Sign up - Porchlight
        </Box>
        <Paper elevation={0} variant="glass" sx={{
          mt: 8,
          px: 3,
          py: 3
        }}>
          <Stack spacing={2} alignItems="stretch">
            <Typography variant="h5" component="h2" align="center" sx={{ fontWeight: 700 }}>
              Welcome to Porchlight
            </Typography>
            <Typography variant="body2" align="center" sx={{ color: 'text.primary' }}>
              Connect with friends and let them know when you’re available by turning your porchlight on or off.
            </Typography>
            <Typography id="signup-form-title" variant="h6" component="h3" align="center" sx={{ fontWeight: 600 }}>
              Sign up
            </Typography>
          </Stack>
          <Box component="form" aria-labelledby="signup-form-title" noValidate onSubmit={handleSignup} sx={{ mt: 2 }} data-testid="signup-form">
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstName"
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
              inputProps={{ 'data-testid': 'signup-first-name' }}
              InputLabelProps={{ sx: { color: 'text.primary' } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="lastName"
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
              inputProps={{ 'data-testid': 'signup-last-name' }}
              InputLabelProps={{ sx: { color: 'text.primary' } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              type="email"
              inputMode="email"
              inputProps={{ 'data-testid': 'signup-email' }}
              InputLabelProps={{ sx: { color: 'text.primary' } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              inputProps={{ 'data-testid': 'signup-password' }}
              InputLabelProps={{ sx: { color: 'text.primary' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              inputProps={{ 'data-testid': 'signup-confirm-password' }}
              InputLabelProps={{ sx: { color: 'text.primary' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <Typography color="error">{error}</Typography>}
            {success && (
              <Alert severity="success">
                Account created successfully! Please check your email to verify your account.
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
              data-testid="signup-submit"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
            <Grid container>
              <Grid item>
                <MuiLink component={RouterLink} to={ROUTES.LOGIN} variant="body2">
                  {"Already have an account? Login"}
                </MuiLink>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default Signup;
